/* eslint-disable no-unused-vars */
import { createSlice } from '@reduxjs/toolkit';
import { dispatch, store } from '../store';
import { callApi } from '../../utils/apiCall';
import { hasFeatureAccess } from '../../utils/featurePermission';
import { updateError } from './user';

const initialState = {
    isLoading: false,
    accounts: [],
    customers: [],
    availableCustomers: [],
    campaigns: [],
    summary: null,
    isSyncing: false,
    // Daily metrics
    dailyMetrics: [],
    dailyMetricsLoading: false,
    // Keywords
    keywords: [],
    keywordsLoading: false,
    // Keyword ROI map — { [criterion_id]: { cost_per_attributed_lead,
    // attributed_leads } }. Merged onto keyword rows before render so the
    // table gets the cost/lead + attribution columns without changing the
    // existing /keywords/ endpoint response shape.
    keywordROIMap: {},
    // Period comparison
    comparison: null,
    comparisonLoading: false,
    // Cross-practice comparison
    crossPractice: null,
    crossPracticeLoading: false,
};

const slice = createSlice({
    name: 'googleAds',
    initialState,
    reducers: {
        setLoading(state, action) {
            state.isLoading = action.payload;
        },
        setAccounts(state, action) {
            state.accounts = action.payload;
        },
        setCustomers(state, action) {
            state.customers = action.payload;
        },
        setAvailableCustomers(state, action) {
            state.availableCustomers = action.payload;
        },
        setCampaigns(state, action) {
            state.campaigns = action.payload;
        },
        setSummary(state, action) {
            state.summary = action.payload;
        },
        setSyncing(state, action) {
            state.isSyncing = action.payload;
        },
        setDailyMetrics(state, action) {
            state.dailyMetrics = action.payload;
        },
        setDailyMetricsLoading(state, action) {
            state.dailyMetricsLoading = action.payload;
        },
        setKeywords(state, action) {
            state.keywords = action.payload;
        },
        setKeywordsLoading(state, action) {
            state.keywordsLoading = action.payload;
        },
        setKeywordROIMap(state, action) {
            state.keywordROIMap = action.payload || {};
        },
        setComparison(state, action) {
            state.comparison = action.payload;
        },
        setComparisonLoading(state, action) {
            state.comparisonLoading = action.payload;
        },
        setCrossPractice(state, action) {
            state.crossPractice = action.payload;
        },
        setCrossPracticeLoading(state, action) {
            state.crossPracticeLoading = action.payload;
        },
        reset() {
            return initialState;
        },
    },
});

export const { reset: resetGoogleAds } = slice.actions;

export default slice.reducer;

// --------------- Thunks ---------------

function getPracticeId() {
    const id = store.getState().practiceTreatmentList?.practiceId;
    if (!id || id === 'null' || id === 'undefined') return null;
    return id;
}

/**
 * Fetch all connected Google Ads accounts for this practice
 */
export function fetchGoogleAdsAccounts() {
    return async () => {
        const practiceId = getPracticeId();
        if (!practiceId) return;
        if (!hasFeatureAccess('google-ads')) return;
        try {
            dispatch(slice.actions.setLoading(true));
            const response = await callApi(`/api/v1/practices/${practiceId}/google-ads-accounts/`);
            if (response.status === 200) {
                dispatch(slice.actions.setAccounts(response.data?.results || []));
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Failed to fetch Google Ads accounts' }));
        } finally {
            dispatch(slice.actions.setLoading(false));
        }
    };
}

/**
 * Get the OAuth URL and redirect the user to Google's consent screen
 */
export function getAuthUrl() {
    return async () => {
        const practiceId = getPracticeId();
        if (!practiceId) return;
        if (!hasFeatureAccess('google-ads')) return;
        try {
            dispatch(slice.actions.setLoading(true));
            const response = await callApi(`/api/v1/practices/${practiceId}/google-ads-accounts/auth_url/`);
            if (response.status === 200 && response.data?.auth_url) {
                window.location.href = response.data.auth_url;
            } else {
                dispatch(updateError({ success: false, message: 'Failed to get Google Ads authorization URL' }));
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Failed to start Google Ads connection' }));
        } finally {
            dispatch(slice.actions.setLoading(false));
        }
    };
}

/**
 * Exchange the OAuth authorization code for tokens (callback handler)
 */
export function exchangeAuthCode(code, onSuccess, onError) {
    return async () => {
        const practiceId = getPracticeId();
        if (!practiceId) return;
        if (!hasFeatureAccess('google-ads')) return;
        try {
            const response = await callApi(
                `/api/v1/practices/${practiceId}/google-ads-accounts/callback/`,
                'post',
                { code }
            );
            if (response.status === 200 || response.status === 201) {
                dispatch(updateError({ success: true, message: 'Google Ads account connected successfully' }));
                if (onSuccess) onSuccess(response.data);
            } else {
                dispatch(updateError({ success: false, message: 'Failed to connect Google Ads account' }));
                if (onError) onError();
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Google Ads authorization failed' }));
            if (onError) onError();
        }
    };
}

/**
 * Fetch available customer accounts after OAuth connection
 */
export function fetchAvailableCustomers(accountId) {
    return async () => {
        const practiceId = getPracticeId();
        if (!practiceId || !accountId) return;
        if (!hasFeatureAccess('google-ads')) return;
        try {
            dispatch(slice.actions.setLoading(true));
            const response = await callApi(
                `/api/v1/practices/${practiceId}/google-ads-accounts/${accountId}/available_customers/`
            );
            if (response.status === 200) {
                dispatch(slice.actions.setAvailableCustomers(response.data?.customers || []));
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Failed to fetch available customers' }));
        } finally {
            dispatch(slice.actions.setLoading(false));
        }
    };
}

/**
 * Connect selected customer accounts to start syncing campaigns
 */
export function connectCustomers(accountId, customerIds) {
    return async () => {
        const practiceId = getPracticeId();
        if (!practiceId || !accountId) return;
        if (!hasFeatureAccess('google-ads')) return;
        try {
            dispatch(slice.actions.setLoading(true));
            const response = await callApi(
                `/api/v1/practices/${practiceId}/google-ads-accounts/${accountId}/connect_customers/`,
                'post',
                { customer_ids: customerIds }
            );
            if (response.status === 200 || response.status === 201) {
                dispatch(updateError({ success: true, message: `Connected ${response.data?.count || customerIds.length} customer account(s)` }));
                // Re-fetch accounts to get updated customer list
                dispatch(fetchGoogleAdsAccounts());
            } else {
                dispatch(updateError({ success: false, message: 'Failed to connect customer accounts' }));
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Failed to connect customer accounts' }));
        } finally {
            dispatch(slice.actions.setLoading(false));
        }
    };
}

/**
 * Disconnect a Google Ads account
 */
export function disconnectAccount(accountId) {
    return async () => {
        const practiceId = getPracticeId();
        if (!practiceId || !accountId) return;
        if (!hasFeatureAccess('google-ads')) return;
        try {
            dispatch(slice.actions.setLoading(true));
            const response = await callApi(
                `/api/v1/practices/${practiceId}/google-ads-accounts/${accountId}/disconnect/`,
                'post'
            );
            if (response.status === 200) {
                dispatch(updateError({ success: true, message: 'Google Ads account disconnected' }));
                dispatch(slice.actions.reset());
                dispatch(fetchGoogleAdsAccounts());
            } else {
                dispatch(updateError({ success: false, message: 'Failed to disconnect account' }));
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Failed to disconnect account' }));
        } finally {
            dispatch(slice.actions.setLoading(false));
        }
    };
}

/**
 * Fetch connected customers for an account
 */
export function fetchCustomers(accountId) {
    return async () => {
        const practiceId = getPracticeId();
        if (!practiceId || !accountId) return;
        if (!hasFeatureAccess('google-ads')) return;
        try {
            const response = await callApi(
                `/api/v1/practices/${practiceId}/google-ads-accounts/${accountId}/customers/`
            );
            if (response.status === 200) {
                dispatch(slice.actions.setCustomers(response.data?.results || []));
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Failed to fetch customers' }));
        }
    };
}

/**
 * Fetch campaigns for a specific customer
 */
export function fetchCampaigns(accountId, customerId, params = {}) {
    return async () => {
        const practiceId = getPracticeId();
        if (!practiceId || !accountId || !customerId) return;
        if (!hasFeatureAccess('google-ads')) return;
        try {
            dispatch(slice.actions.setLoading(true));
            const queryParams = new URLSearchParams();
            if (params.status) queryParams.append('status', params.status);
            if (params.campaign_type) queryParams.append('campaign_type', params.campaign_type);
            if (params.ordering) queryParams.append('ordering', params.ordering);
            if (params.date_after) queryParams.append('date_after', params.date_after);
            if (params.date_before) queryParams.append('date_before', params.date_before);
            const queryString = queryParams.toString();
            const url = `/api/v1/practices/${practiceId}/google-ads-accounts/${accountId}/customers/${customerId}/campaigns/${queryString ? `?${queryString}` : ''}`;
            const response = await callApi(url);
            if (response.status === 200) {
                dispatch(slice.actions.setCampaigns(response.data?.results || []));
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Failed to fetch campaigns' }));
        } finally {
            dispatch(slice.actions.setLoading(false));
        }
    };
}

/**
 * Fetch campaign summary (aggregated metrics for ENABLED campaigns)
 */
export function fetchCampaignSummary(accountId, customerId, params = {}) {
    return async () => {
        const practiceId = getPracticeId();
        if (!practiceId || !accountId || !customerId) return;
        if (!hasFeatureAccess('google-ads')) return;
        try {
            const queryParams = new URLSearchParams();
            if (params.date_after) queryParams.append('date_after', params.date_after);
            if (params.date_before) queryParams.append('date_before', params.date_before);
            const queryString = queryParams.toString();
            const response = await callApi(
                `/api/v1/practices/${practiceId}/google-ads-accounts/${accountId}/customers/${customerId}/campaigns/summary/${queryString ? `?${queryString}` : ''}`
            );
            if (response.status === 200) {
                dispatch(slice.actions.setSummary(response.data));
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Failed to fetch campaign summary' }));
        }
    };
}

/**
 * Trigger a manual sync for a customer, then refresh data
 */
export function syncCustomer(accountId, customerId, dateParams = {}) {
    return async () => {
        const practiceId = getPracticeId();
        if (!practiceId || !accountId || !customerId) return;
        if (!hasFeatureAccess('google-ads')) return;
        try {
            dispatch(slice.actions.setSyncing(true));
            const response = await callApi(
                `/api/v1/practices/${practiceId}/google-ads-accounts/${accountId}/customers/${customerId}/sync/`,
                'post'
            );
            if (response.status === 200) {
                dispatch(updateError({ success: true, message: 'Sync started. Refreshing data...' }));
                // Wait for Celery task to complete, then re-fetch with current date filters
                await new Promise((resolve) => { setTimeout(resolve, 3000); });
                dispatch(fetchCampaigns(accountId, customerId, dateParams));
                dispatch(fetchCampaignSummary(accountId, customerId, dateParams));
                dispatch(fetchCustomers(accountId));
            } else {
                dispatch(updateError({ success: false, message: 'Failed to trigger sync' }));
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Failed to sync' }));
        } finally {
            dispatch(slice.actions.setSyncing(false));
        }
    };
}

/**
 * Fetch daily metrics for a specific customer
 */
export function fetchDailyMetrics(accountId, customerId, params = {}) {
    return async () => {
        const practiceId = getPracticeId();
        if (!practiceId || !accountId || !customerId) return;
        if (!hasFeatureAccess('google-ads')) return;
        try {
            dispatch(slice.actions.setDailyMetricsLoading(true));
            const queryParams = new URLSearchParams();
            if (params.date_after) queryParams.append('date_after', params.date_after);
            if (params.date_before) queryParams.append('date_before', params.date_before);
            if (params.campaign_id) queryParams.append('campaign_id', params.campaign_id);
            const queryString = queryParams.toString();
            const url = `/api/v1/practices/${practiceId}/google-ads-accounts/${accountId}/customers/${customerId}/daily-metrics/${queryString ? `?${queryString}` : ''}`;
            const response = await callApi(url);
            if (response.status === 200) {
                dispatch(slice.actions.setDailyMetrics(response.data?.results || response.data || []));
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Failed to fetch daily metrics' }));
        } finally {
            dispatch(slice.actions.setDailyMetricsLoading(false));
        }
    };
}

/**
 * Fetch period comparison for daily metrics
 */
export function fetchComparison(accountId, customerId, params = {}) {
    return async () => {
        const practiceId = getPracticeId();
        if (!practiceId || !accountId || !customerId) return;
        if (!hasFeatureAccess('google-ads')) return;
        try {
            dispatch(slice.actions.setComparisonLoading(true));
            const queryParams = new URLSearchParams();
            if (params.period_a_start) queryParams.append('period_a_start', params.period_a_start);
            if (params.period_a_end) queryParams.append('period_a_end', params.period_a_end);
            if (params.period_b_start) queryParams.append('period_b_start', params.period_b_start);
            if (params.period_b_end) queryParams.append('period_b_end', params.period_b_end);
            const queryString = queryParams.toString();
            const url = `/api/v1/practices/${practiceId}/google-ads-accounts/${accountId}/customers/${customerId}/daily-metrics/compare/${queryString ? `?${queryString}` : ''}`;
            const response = await callApi(url);
            if (response.status === 200) {
                dispatch(slice.actions.setComparison(response.data));
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Failed to fetch comparison data' }));
        } finally {
            dispatch(slice.actions.setComparisonLoading(false));
        }
    };
}

/**
 * Fetch keyword performance data for a specific customer
 */
export function fetchKeywords(accountId, customerId, params = {}) {
    return async () => {
        const practiceId = getPracticeId();
        if (!practiceId || !accountId || !customerId) return;
        if (!hasFeatureAccess('google-ads')) return;
        try {
            dispatch(slice.actions.setKeywordsLoading(true));
            const queryParams = new URLSearchParams();
            if (params.keyword) queryParams.append('keyword', params.keyword);
            if (params.match_type) queryParams.append('match_type', params.match_type);
            if (params.campaign_id) queryParams.append('campaign_id', params.campaign_id);
            if (params.min_clicks) queryParams.append('min_clicks', params.min_clicks);
            if (params.ordering) queryParams.append('ordering', params.ordering);
            if (params.date_after) queryParams.append('date_after', params.date_after);
            if (params.date_before) queryParams.append('date_before', params.date_before);
            const queryString = queryParams.toString();
            const url = `/api/v1/practices/${practiceId}/google-ads-accounts/${accountId}/customers/${customerId}/keywords/${queryString ? `?${queryString}` : ''}`;
            const response = await callApi(url);
            if (response.status === 200) {
                dispatch(slice.actions.setKeywords(response.data?.results || response.data || []));
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Failed to fetch keywords' }));
        } finally {
            dispatch(slice.actions.setKeywordsLoading(false));
        }
    };
}

/**
 * Set a campaign's status to PAUSED or ENABLED. SUPERADMIN-ONLY at the API
 * level (backend returns 403 otherwise). Mutates the advertiser's Google
 * Ads account, so the callsite should also gate the button on
 * `useAuth().user.is_superuser`. Returns true on success; false on any
 * error (reason surfaced via updateError).
 */
export function setCampaignStatus(accountId, customerId, campaignPk, newStatus, dateParams = {}) {
    return async () => {
        const practiceId = getPracticeId();
        if (!practiceId || !accountId || !customerId || !campaignPk) return false;
        if (!hasFeatureAccess('google-ads')) return false;
        if (!['PAUSED', 'ENABLED'].includes(newStatus)) {
            dispatch(updateError({ success: false, message: 'Invalid status — must be PAUSED or ENABLED.' }));
            return false;
        }
        try {
            // callApi is positional: (uri, method, data, isUnAuthorized, options).
            // The earlier object-form invocation was silently broken — data
            // was never sent, and method coerced to a garbage string.
            const url = `/api/v1/practices/${practiceId}/google-ads-accounts/${accountId}/customers/${customerId}/campaigns/${campaignPk}/set-status/`;
            const response = await callApi(url, 'POST', { status: newStatus });
            if (response.status === 200) {
                // Refresh campaigns list so the status badge updates.
                // Forward the caller's dateParams so the user's date filter
                // isn't silently wiped after every toggle.
                dispatch(fetchCampaigns(accountId, customerId, dateParams || {}));
                return true;
            }
            // 401 here means either the CRM JWT couldn't refresh (rare —
            // callApi auto-refreshes once) OR Google rejected the upstream
            // OAuth token. Treat as reconnect-required since that's the
            // only actionable recovery path for the user.
            const fallbackByStatus = {
                403: 'Only superadmins can change campaign status.',
                401: 'Google Ads account needs reconnecting.',
                429: 'Google Ads is rate-limited — try again in a few minutes.',
                502: 'Google Ads is temporarily unavailable — try again shortly.',
            };
            const msg = response.data?.error
                || fallbackByStatus[response.status]
                || 'Failed to change campaign status.';
            dispatch(updateError({ success: false, message: msg }));
            return false;
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Failed to change campaign status.' }));
            return false;
        }
    };
}

/**
 * Toggle offline conversion upload for a customer. SUPERADMIN-ONLY at the
 * API level. Writing conversions to Google Ads affects Smart Bidding +
 * billing optimisation, so this is gated tighter than pause/resume.
 *
 * Args:
 *     enabled: boolean
 *     conversionActionResourceName: "customers/{cid}/conversionActions/{caid}"
 *         — required when enabling. Advertiser must create the
 *         conversion action in the Google Ads UI first and paste the
 *         resource name into our superadmin panel.
 */
export function setConversionUploadConfig(accountId, customerId, {
    enabled,
    conversionActionResourceName,
}) {
    return async () => {
        const practiceId = getPracticeId();
        if (!practiceId || !accountId || !customerId) return false;
        if (!hasFeatureAccess('google-ads')) return false;
        try {
            const url = `/api/v1/practices/${practiceId}/google-ads-accounts/${accountId}/customers/${customerId}/conversion-upload-config/`;
            const response = await callApi(url, 'POST', {
                enabled,
                conversion_action_resource_name: conversionActionResourceName || '',
            });
            if (response.status === 200) {
                return response.data;
            }
            const fallbackByStatus = {
                403: 'Only superadmins can toggle conversion upload.',
                400: response.data?.error || 'Missing conversion action resource name.',
            };
            const msg = response.data?.error
                || fallbackByStatus[response.status]
                || 'Failed to update conversion upload config.';
            dispatch(updateError({ success: false, message: msg }));
            return false;
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Failed to update conversion upload config.' }));
            return false;
        }
    };
}

/**
 * Fetch keyword ROI — joins keyword spend against attributed leads via
 * utm_campaign match. Separate endpoint from /keywords/ because it's a
 * compute-heavier query; we only call it when the Keywords tab is open.
 * Returns rows with { cost_per_attributed_lead, attributed_leads }
 * fields which the KeywordsTable column set consumes. Response is an
 * array — not paginated — because the full keyword set is already
 * bounded by the backend sync cap.
 */
export function fetchKeywordROI(accountId, customerId, params = {}) {
    return async () => {
        const practiceId = getPracticeId();
        if (!practiceId || !accountId || !customerId) return;
        if (!hasFeatureAccess('google-ads')) return;
        try {
            dispatch(slice.actions.setKeywordsLoading(true));
            const query = new URLSearchParams();
            if (params.start) query.append('start', params.start);
            if (params.end) query.append('end', params.end);
            const qs = query.toString();
            const url = `/api/v1/practices/${practiceId}/google-ads-accounts/${accountId}/customers/${customerId}/keyword-roi/${qs ? `?${qs}` : ''}`;
            const response = await callApi(url);
            if (response.status === 200) {
                // Merge ROI fields onto existing keywords by criterion_id so
                // the table keeps existing columns and just gains the new ones.
                const roi = response.data || [];
                dispatch(slice.actions.setKeywordROIMap(
                    Object.fromEntries(
                        roi.map((r) => [String(r.keyword_id), {
                            cost_per_attributed_lead: r.cost_per_attributed_lead,
                            attributed_leads: r.attributed_leads,
                        }])
                    )
                ));
            } else if (response.status !== 404) {
                // 404 = backend doesn't have the endpoint yet (old deploy).
                // Silently skip rather than showing an error banner.
                dispatch(updateError({ success: false, message: response.data?.error || 'Failed to load keyword ROI' }));
            }
        } catch (error) {
            // Network-layer failure (timeout, offline, DNS) — ROI is
            // supplementary; the keyword table still renders without it,
            // so we deliberately don't show an error banner. Capture to
            // Sentry so support has telemetry when a user reports a
            // missing ROI column but the UI stays quiet.
            // eslint-disable-next-line no-console
            console.warn('[google_ads] keyword ROI fetch failed:', error?.message);
            try {
                // Lazy import so the slice doesn't load Sentry on first paint.
                const Sentry = await import('@sentry/react');
                Sentry.captureException(error, {
                    tags: { area: 'google_ads', feature: 'keyword_roi' },
                });
            } catch {
                // Sentry unavailable — best effort only.
            }
        } finally {
            dispatch(slice.actions.setKeywordsLoading(false));
        }
    };
}

/**
 * Fetch cross-practice comparison (top-level endpoint, no practice ID)
 */
export function fetchCrossPracticeComparison(params = {}) {
    return async () => {
        if (!hasFeatureAccess('google-ads')) return;
        try {
            dispatch(slice.actions.setCrossPracticeLoading(true));
            const queryParams = new URLSearchParams();
            if (params.period_a_start) queryParams.append('period_a_start', params.period_a_start);
            if (params.period_a_end) queryParams.append('period_a_end', params.period_a_end);
            if (params.period_b_start) queryParams.append('period_b_start', params.period_b_start);
            if (params.period_b_end) queryParams.append('period_b_end', params.period_b_end);
            const queryString = queryParams.toString();
            const url = `/api/v1/google-ads/cross-practice-compare/compare/${queryString ? `?${queryString}` : ''}`;
            const response = await callApi(url);
            if (response.status === 200) {
                dispatch(slice.actions.setCrossPractice(response.data));
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Failed to fetch cross-practice comparison' }));
        } finally {
            dispatch(slice.actions.setCrossPracticeLoading(false));
        }
    };
}
